// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

// SERVICES
import { ClientService } from '../services/client.service';

// ACTIONS AND STATES
import * as globalActions from '../../../global/store/actions';
import { State } from '../../../root-state';
import * as clientActions from './actions';

// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../../models/common/modal/modal-config';

// CONSTANT
import { CLIENT_METHOD_CONSTANT, CLIENT_NGRX_CONSTANTS } from '../../../../constants/ngrx-store/client';
import { API_METHOD_CONSTANTS } from '../../../../constants/ngrx-store/core';


/**
 * This effect is for listen client actions, execute client service call and dispatch in client reducer.
 * For client api call purpose.
 *
 * @author Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 01-05-2020(Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>)  created client effect
 */
@Injectable()
export class ClientEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private clientService: ClientService) { }

  /**
      * client Effect
      * @param type : client action name.
      * @param payload : object to identify common client method base on module and method.
      * @returns Empty action dispatch to reducer.
      */
  clientCommonEffect$ = createEffect(() => this.actions$.pipe(
    ofType<clientActions.ClientCommonAction>(clientActions.ActionTypes.CLIENT_COMMON),
    withLatestFrom(this.store),
    mergeMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common = CLIENT_NGRX_CONSTANTS[action.payload.module][action.payload.method];

      // avoid service call if data already exists in store.
      // if (state[common.STATE]) {
      //   return of(new globalActions.EmptyAction({}));
      // }
      // Only sending payload data

      return this.clientService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {
            // throwing an error on if the status is not 200
            // if (data && data.status !== 200) { return throwError(data); }
            let actions: any[] = [];


            // dispatch client success action
            actions = [new clientActions.ClientCommonSuccessAction({ data, common })];
            if (common.SUCCESS_NOTIFICATION) {
              if (data && data.status != 202) {
                // dispatch popup notification for open success popup
                actions.push(new globalActions.PopupNotificationAction({
                  actionType: clientActions.ActionTypes.CLIENT_COMMON, type: ModalType.SUCCESS,
                  callback: action.payload.callback, title: common.TITLE, message: common.SUCCESS,
                  code: `${common.SUCCESS}`, response: data,
                }));
              } else {
                action.payload.callback && action.payload.callback({ success: true, response: data });
              }
            } else {
              action.payload.callback && action.payload.callback({ success: true, data, response: data });
            }

            return actions;
          }),
          catchError(error => {
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: clientActions.ActionTypes.CLIENT_COMMON_SUCCESS, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: (error || error.error),
              // code: `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }));
          },
          ));
    }),
  ));

  /**
   *  Client By Id effect
   *  @param type : client action
   *  @param payload : object to identify common client method base on module and method.
   *  @returns Empty action dispatch to reducer.
   */
  clientByIdEffect$ = createEffect(() => this.actions$.pipe(
    ofType<clientActions.ClientByIdAction>(clientActions.ActionTypes.CLIENT_BY_ID),
    withLatestFrom(this.store),
    switchMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common = CLIENT_NGRX_CONSTANTS[action.payload.module][action.payload.method];

      // avoid service call if data already exists in store.
      // if (state && state.client && state.client.clientById && state.client.clientById[action.payload.data.client_id]) {
      //   return of(new globalActions.EmptyAction({}));
      // }
      // Only sending payload data

      return this.clientService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {
            // throwing an error on if the status is not 200
            if (data.status !== 200) { return throwError(() => data); }
            let actions: any[] = [];


            // dispatch clientById success action
            actions = [new clientActions.ClientByIdSuccessAction({ data, common })];

            if (common.SUCCESS_NOTIFICATION) {
              // dispatch popup notification for open success popup
              actions.push(new globalActions.PopupNotificationAction({
                actionType: clientActions.ActionTypes.CLIENT_COMMON, type: ModalType.SUCCESS,
                callback: action.payload.callback, title: common.TITLE, message: common.SUCCESS,
                code: `${common.SUCCESS}`, response: data,
              }));
            } else {
              if (action.payload && action.payload.callback) {
                action.payload.callback({ success: true, data });
              }
            }

            return actions;
          }),
          catchError(error => {
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: clientActions.ActionTypes.CLIENT_BY_ID_SUCCESS, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: (error || error.error),
              // code: `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }));
          },
          ));
    }),
  ));


  /**
    * Location By Client Effect
    * @param type : GET_LOCATION_BY_CLIENT Action
    */
  getLocationByClientEffect$ = createEffect(() => this.actions$.pipe(
    ofType<clientActions.ClientCommonAction>(clientActions.ActionTypes.GET_LOCATION_BY_CLIENT),
    withLatestFrom(this.store),
    mergeMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      const common = CLIENT_NGRX_CONSTANTS[CLIENT_METHOD_CONSTANT.LOCATION][API_METHOD_CONSTANTS.GET_LOOKUP];

      // avoid service call if data already exists in store.
      // if (state.client && state.client.locationLookup && state.client.locationLookup[action.payload.data.client_id]) {
      //   return of(new globalActions.EmptyAction({}));
      // }

      return this.clientService.getLocationList(action.payload.data)
        .pipe(
          mergeMap((data: any) => {
            let actions: any[] = [];
            // dispatch master success action
            actions = [new clientActions.GetLocationByClientSuccessAction({ action: action.payload.data, data, common })];

            return actions;
          }),
          catchError(error => {
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: clientActions.ActionTypes.GET_LOCATION_BY_CLIENT, type: ModalType.ERROR,
              message: error.error.message, callback: action.payload.callback, error: error.error,
              title: common.TITLE,
            }));
          },
          ));
    }),
  ));
}
